import { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RequestFilters, RangeFilter } from 'utils/types/filters';
import RequestsTable from 'Organisms/RequestsTable/RequestsTable';
import RequestsTableFooter from 'Organisms/RequestsTable/RequestsTableFooter';
import RequestsTableOperationHeader from 'Organisms/RequestsTable/RequestsTableOperationHeader/RequestsTableOperationHeader';
import { REQUESTS_TABLE } from 'utils/constants';
import { ACTIVE_REQUEST_STATUSES } from 'utils/constants/request';
import { requestsSelectors } from 'state/normalized/requests/requestsSlice';
import useRequestsTable from 'Hooks/Requests/useRequestsTable';
import ActiveRequestsSidePanelFilters from './ActiveRequestsSidePanelFilters';
import {
  selectFiltersSettingsByType,
  createFilterSetting,
  updateFilterSetting,
} from 'state/Settings/Filters/FiltersSlice';
import { setFilters } from 'state/Requests/requestSlice';
import { RequestStatus } from 'utils/types/request';
import { REQUEST_TABLE_FILTERS, SETTINGS_FILTERS } from 'utils/constants';

const ActiveRequestsTable = () => {
  const dispatch = useDispatch();
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [areFiltersApplied, setAreFiltersApplied] = useState<boolean>(false);
  const [areFiltersLoading, setAreFiltersLoading] = useState<boolean>(false);

  const toggleFilters = () => setShowFilters((prev) => !prev);
  const filtersSettings = useSelector(
    selectFiltersSettingsByType(REQUESTS_TABLE.ACTIVE_REQUESTS)
  );
  const appliedFilters = useMemo(
    () =>
      filtersSettings
        ? (filtersSettings.settings as RequestFilters)
        : ({} as RequestFilters),
    [filtersSettings]
  );

  const {
    requests,
    status,
    canFetchMore,
    onFetchMore,
    searchRequests,
    isSearchApplied,
  } = useRequestsTable({
    filterType: SETTINGS_FILTERS.ACTIVE_REQUESTS_TABLE,
    statuses:
      (appliedFilters[
        REQUEST_TABLE_FILTERS.STATUS
      ] as readonly RequestStatus[]) || ACTIVE_REQUEST_STATUSES,
    filterAppliedRequesterIds: appliedFilters[
      REQUEST_TABLE_FILTERS.REQUESTER
    ] as string[],
    projectIds: appliedFilters[REQUEST_TABLE_FILTERS.PROJECTS] as string[],
    filterAppliedOwnerIds: appliedFilters[
      REQUEST_TABLE_FILTERS.OWNERS
    ] as string[],
    filterAppliedBusinessUnitIds: appliedFilters[
      REQUEST_TABLE_FILTERS.BUSINESS_TEAMS
    ] as string[],
    numberOfProjects: appliedFilters[
      REQUEST_TABLE_FILTERS.LINKED_PROJECTS
    ] as string[],
    filterAppliedPriority: appliedFilters[
      REQUEST_TABLE_FILTERS.PRIORITY
    ] as string[],
    formIds: appliedFilters[REQUEST_TABLE_FILTERS.FORM] as string[],
    updatedAt: appliedFilters[REQUEST_TABLE_FILTERS.UPDATED_AT] as RangeFilter,
    createdAt: appliedFilters[REQUEST_TABLE_FILTERS.CREATED_AT] as RangeFilter,
    submittedAt: appliedFilters[
      REQUEST_TABLE_FILTERS.SUBMITTED_AT
    ] as RangeFilter,
    decisionDate: appliedFilters[
      REQUEST_TABLE_FILTERS.DECISION_DATE
    ] as RangeFilter,
  });
  const totalCount = useSelector(requestsSelectors.selectTotalRequests);
  const loadedCount = useSelector(requestsSelectors.selectLoadedRequests);

  useEffect(() => {
    if (appliedFilters) {
      dispatch(setFilters(appliedFilters, REQUESTS_TABLE.ACTIVE_REQUESTS));
      setAreFiltersApplied(Object.keys(appliedFilters).length > 0);
    }
  }, [appliedFilters, dispatch]);

  const handleSaveFiltersSettings = async (filters: RequestFilters) => {
    if (filtersSettings && filtersSettings.id) {
      await dispatch(
        updateFilterSetting({
          id: filtersSettings.id,
          updateFields: { filter_settings: filters },
        })
      );
    } else {
      await dispatch(
        createFilterSetting({
          filter_type: REQUESTS_TABLE.ACTIVE_REQUESTS,
          filter_settings: filters,
        })
      );
    }
    setAreFiltersLoading(false);
  };

  const handleUpdateFilters = (filters: RequestFilters) => {
    setAreFiltersApplied(Object.keys(filters).length > 0);
    setAreFiltersLoading(true);
    dispatch(setFilters(filters, REQUESTS_TABLE.ACTIVE_REQUESTS));
    handleSaveFiltersSettings(filters);
  };
  return (
    <>
      <ActiveRequestsSidePanelFilters
        filters={appliedFilters}
        onClose={toggleFilters}
        open={showFilters}
        onUpdateFilters={handleUpdateFilters}
      />
      <RequestsTableOperationHeader
        handleUpdateFilters={handleUpdateFilters}
        appliedFilters={appliedFilters}
        onSearchChange={searchRequests}
        showFilters={showFilters}
        toggleFilters={toggleFilters}
      />
      <RequestsTable
        requests={requests}
        status={status}
        canFetchMore={canFetchMore}
        onFetchMore={onFetchMore}
        isSearchApplied={isSearchApplied}
        areFiltersApplied={areFiltersApplied}
        areFiltersLoading={areFiltersLoading}
      />
      <RequestsTableFooter total={totalCount} loaded={loadedCount} />
    </>
  );
};

export default ActiveRequestsTable;
